import api, { CHAT_API_URL } from './api';
import type {
  AddCallParticipantsInput,
  CallActionInput,
  CallSnapshot,
  CallStartInput,
  LiveKitCredentials,
  P2pOffer,
} from '../types/calls';
import { normaliseCallSnapshot } from '../types/calls';
import type {
  CallActionV2,
  CallCreateV2,
  CallParticipantsV2,
  CallSnapshotV2,
} from './generated/calls-v2';

export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export function createCallEventId(prefix = 'call'): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const startCall = (input: CallStartInput) =>
  api.post<CallSnapshot | CallSnapshotV2>('/calls', {
    clientEventId: input.clientEventId,
    conversationId: input.conversationId,
    participantUserIds: input.participantUserIds,
  }).then(r => normaliseCallSnapshot(r.data));

export const getCallSnapshot = (callId: string) =>
  api.get<CallSnapshot | CallSnapshotV2>(`/calls/${encodeURIComponent(callId)}`).then(r => normaliseCallSnapshot(r.data));

export const actOnCall = (callId: string, input: CallActionInput) =>
  api.post<CallSnapshot | CallSnapshotV2>(`/calls/${encodeURIComponent(callId)}/actions`, {
    action: ({ answer: 'accept', 'participant-joined': 'join', 'participant-left': 'leave' } as Record<string, string>)[input.action] ?? input.action,
    eventId: input.eventId,
    expectedVersion: input.expectedVersion,
  }).then(r => normaliseCallSnapshot(r.data));

/**
 * Unload-safe lifecycle mutation. `sendBeacon` cannot carry the bearer header,
 * so use authenticated fetch with keepalive and the same canonical payload as
 * actOnCall. The server event id makes duplicate pagehide/beforeunload attempts
 * harmless.
 */
export function actOnCallKeepalive(callId: string, input: CallActionInput): void {
  try {
    const token = localStorage.getItem('accessToken');
    void fetch(`${CHAT_API_URL}/calls/${encodeURIComponent(callId)}/actions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        action: ({ answer: 'accept', 'participant-joined': 'join', 'participant-left': 'leave' } as Record<string, string>)[input.action] ?? input.action,
        eventId: input.eventId,
        expectedVersion: input.expectedVersion,
      }),
      keepalive: true,
    }).catch(() => { /* best-effort during renderer teardown */ });
  } catch { /* noop */ }
}

export const addCallParticipants = (callId: string, input: AddCallParticipantsInput) =>
  api.post<CallSnapshot | CallSnapshotV2>(`/calls/${encodeURIComponent(callId)}/participants`, {
    eventId: input.eventId,
    expectedVersion: input.expectedVersion,
    userIds: input.participantUserIds,
  }).then(r => normaliseCallSnapshot(r.data));

export const getP2pOffer = (callId: string) =>
  api.get<P2pOffer>(`/calls/${encodeURIComponent(callId)}/signaling/offer`).then(r => r.data);

export const getLiveKitCredentials = (callId: string) =>
  api.get<LiveKitCredentials>(`/calls/${encodeURIComponent(callId)}/livekit-token`).then(r => r.data);

export const syncCalls = (afterVersion?: number) =>
  api.get<{ calls: Array<CallSnapshot | CallSnapshotV2> }>('/calls/sync', {
    params: afterVersion == null ? undefined : { after_version: afterVersion },
  }).then(r => ({ calls: r.data.calls.map(normaliseCallSnapshot) }));

export const startCallV2 = (input: CallCreateV2) =>
  api.post<CallSnapshotV2>('/calls', input).then(r => r.data);

export const getCallSnapshotV2 = (callId: string) =>
  api.get<CallSnapshotV2>(`/calls/${encodeURIComponent(callId)}`).then(r => r.data);

export const actOnCallV2 = (callId: string, input: CallActionV2) =>
  api.post<CallSnapshotV2>(`/calls/${encodeURIComponent(callId)}/actions`, input).then(r => r.data);

export const addCallParticipantsV2 = (callId: string, input: CallParticipantsV2) =>
  api.post<CallSnapshotV2>(`/calls/${encodeURIComponent(callId)}/participants`, input).then(r => r.data);

export const syncCallsV2 = (afterVersion?: number) =>
  api.get<{ calls: CallSnapshotV2[] }>('/calls/sync', {
    params: afterVersion == null ? undefined : { after_version: afterVersion },
  }).then(r => r.data);

/**
 * Fetch the ICE server list (self-hosted STUN/TURN) the RTCPeerConnection
 * should use. A missing server configuration is surfaced to the call UI; it
 * never silently routes media through a public provider.
 */
export async function getIceServers(): Promise<IceServer[]> {
  try {
    const r = await api.get<{ iceServers: IceServer[] }>('/calls/ice-servers');
    const servers = r.data?.iceServers;
    if (!servers?.length) throw new Error('RTC configuration unavailable');
    return servers;
  } catch (error) {
    throw new Error('RTC configuration unavailable', { cause: error });
  }
}

export type CallDirection = 'incoming' | 'outgoing';
export type CallType = 'voice' | 'video';
export type CallOutcome =
  | 'completed' | 'missed' | 'no_answer' | 'declined' | 'busy' | 'cancelled' | 'failed';

export interface CallLogCreate {
  call_id: string;
  peer_user_id: string | null;
  conversation_id?: string | null;
  direction: CallDirection;
  call_type: CallType;
  outcome: CallOutcome;
  started_at?: string | null;
  answered_at?: string | null;
  ended_at?: string | null;
  duration_s: number;
}

export interface CallEventCreate {
  call_id: string;
  conversation_id: string;
  call_type: CallType;
  outcome: CallOutcome;
  is_group: boolean;
  duration_s: number;
  started_at?: string | null;
  ended_at?: string | null;
  peer_user_id?: string | null;
}

export interface CallLogRecord {
  id: string;
  call_id: string;
  peer_user_id: string | null;
  peer_name: string | null;
  peer_avatar_url: string | null;
  conversation_id: string | null;
  direction: CallDirection;
  call_type: CallType;
  outcome: CallOutcome;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  duration_s: number;
  created_at: string | null;
  is_group?: boolean;
}

/** Persist a finalized call record (idempotent server-side on call_id+owner). */
export const logCall = (payload: CallLogCreate) =>
  api.post<CallLogRecord>('/calls/log', payload).then(r => r.data);

export const logCallEvent = (payload: CallEventCreate) =>
  api.post('/calls/event', payload).then(r => r.data);

/** Fetch the viewer's call history, newest first. */
export const getCallHistory = (params?: { before_id?: string; limit?: number }) =>
  api.get<CallLogRecord[]>('/calls/history', { params }).then(r => r.data);

/** Soft-delete one row. */
export const deleteCallLog = (logId: string) =>
  api.delete(`/calls/log/${encodeURIComponent(logId)}`).then(() => undefined);

/** Clear the whole call log. */
export const clearCallHistory = () =>
  api.delete('/calls/history').then(() => undefined);

/**
 * Unload-safe variant of logCall: native fetch with keepalive:true so the
 * request survives the page closing (axios/XHR would be aborted). Replicates
 * the axios auth header (CHAT_API_URL already ends in /chat-api).
 */
export function logCallKeepalive(payload: CallLogCreate): void {
  try {
    const token = localStorage.getItem('accessToken');
    void fetch(`${CHAT_API_URL}/calls/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  } catch { /* noop */ }
}

export interface ScheduledCallRecord {
  id: string;
  conversation_id: string | null;
  scheduled_at: string | null;   // UTC ISO with trailing 'Z'
  title: string | null;
  status: 'scheduled' | 'cancelled';
  created_at: string | null;
  role: 'owner' | 'peer';        // relative to the viewer
  target_user_id: string | null; // the other party (whom you'd call)
  target_name: string | null;
  target_avatar_url: string | null;
}

/** Schedule a future 1:1 voice call. scheduled_at must be a true-UTC ISO string. */
export const scheduleCall = (body: {
  peer_user_id: string;
  scheduled_at: string;
  title?: string | null;
  conversation_id?: string | null;
}) => api.post<ScheduledCallRecord>('/calls/scheduled', body).then(r => r.data);

/** The viewer's upcoming scheduled calls, soonest first. */
export const listScheduledCalls = () =>
  api.get<ScheduledCallRecord[]>('/calls/scheduled').then(r => r.data);

/** Owner cancels a scheduled call. */
export const cancelScheduledCall = (id: string) =>
  api.delete(`/calls/scheduled/${encodeURIComponent(id)}`).then(() => undefined);
